import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import GoogleLoginButton from '../components/GoogleLoginButton.jsx';
import { 
  LogoText, 
  Button, 
  Card, 
  Text, 
  Heading, 
  Container,
  Spinner,
  Icon
} from '../components/ui/index.jsx';

export default function SignIn() {
    const navigate = useNavigate();
    const { isAuthenticated, authType, loading } = useAuth();

    // Redirect if already authenticated
    React.useEffect(() => {
        if (isAuthenticated && authType === 'authenticated') {
            navigate('/');
        }
    }, [isAuthenticated, authType, navigate]);

    // Handle OAuth callback parameters on initial load
    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const oauthSuccess = urlParams.get('oauth_success');
        const userEmail = urlParams.get('user_email');
        const userName = urlParams.get('user_name');

        if (oauthSuccess === 'true' && userEmail) {
            // Clear URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // The AuthContext should handle the authentication state update
            // and the first useEffect will handle the redirect
        }
    }, []);

    const handleSignInSuccess = () => {
        // This shouldn't be called since signInWithGoogle just redirects
        // But kept for compatibility
        navigate('/');
    };

    const handleSignInError = (error) => {
        window.showToast?.('Sign-in failed. Please try again.', 'error');
    };

    // Show loading while auth is being processed
    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="lg" className="mb-4" />
                    <Text variant="secondary">Loading...</Text>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white selection:bg-[#7B61FF] selection:text-white relative overflow-hidden flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            <Container size="md" className="relative z-10">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    {/* Logo */}
                    <div className="flex justify-center mb-8">
                        <LogoText 
                            size="lg" 
                            onClick={() => navigate('/')}
                            className="cursor-pointer"
                        />
                    </div>
                    
                    <Heading level={2} className="text-center mb-2 whitespace-nowrap">
                        Sign in to The Architect
                    </Heading>
                    <Text variant="secondary" className="text-center">
                        Transform your ideas into AI-ready prompts
                    </Text>
                </div>

                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <Card shadow className="p-8">
                        <div className="space-y-6">
                            {/* Sign-in benefits */}
                            <div className="text-center">
                                <Heading level={3} className="mb-4">
                                    Get started with your free account
                                </Heading>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-center">
                                        <Icon name="star" size="xs" className="mr-2 text-[#7B61FF]" />
                                        <Text variant="secondary" size="sm">
                                            Analyze unlimited app ideas
                                        </Text>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <Icon name="target" size="xs" className="mr-2 text-[#7B61FF]" />
                                        <Text variant="secondary" size="sm">
                                            Get hundreds of specific prompts
                                        </Text>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <Icon name="creditCard" size="xs" className="mr-2 text-[#7B61FF]" />
                                        <Text variant="secondary" size="sm">
                                            Free credits to get started
                                        </Text>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <Icon name="chart" size="xs" className="mr-2 text-[#7B61FF]" />
                                        <Text variant="secondary" size="sm">
                                            Track your analysis history
                                        </Text>
                                    </div>
                                </div>
                            </div>

                            {/* Google Sign-in Button */}
                            <div className="mt-6">
                                <GoogleLoginButton 
                                    className="w-full justify-center"
                                    size="large"
                                    variant="primary"
                                    showText={true}
                                    onSuccess={handleSignInSuccess}
                                    onError={handleSignInError}
                                />
                            </div>

                            {/* Terms */}
                            <div className="text-center">
                                <Text variant="muted" size="xs">
                                    By signing in, you agree to our{' '}
                                    <button
                                        onClick={() => navigate('/terms')}
                                        className="text-[#7B61FF] hover:text-[#8A6DFF] underline"
                                    >
                                        Terms of Service
                                    </button>{' '}
                                    and{' '}
                                    <button
                                        onClick={() => navigate('/privacy')}
                                        className="text-[#7B61FF] hover:text-[#8A6DFF] underline"
                                    >
                                        Privacy Policy
                                    </button>
                                </Text>
                            </div>
                        </div>
                    </Card>

                    {/* Back to Home */}
                    <div className="mt-6 text-center">
                        <Button
                            onClick={() => navigate('/')}
                            variant="ghost"
                            size="sm"
                            className="flex items-center"
                        >
                            <Icon name="arrowLeft" size="xs" className="mr-1" />
                            Back to Home
                        </Button>
                    </div>
                </div>
            </Container>
        </div>
    );
} 